import { Grid, IconButton, InputAdornment, TextField } from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Visibility,
  Person,
  Email,
  Phone,
  Lock,
} from "@mui/icons-material";
import { CRUDContext } from "./UsersTable";
import { endpoints } from "../../Utils/UrlEnum";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { UserFormProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import Config from "../../Utils/Config";
import { ClientAsUserSchema } from "../../Utils/Models";
import { onFormError } from "../../Utils/Utils";

export default function UserForm(props: UserFormProps) {
  const userContext: any = useContext(
    props.selectedUser ? props.selectedUser : CRUDContext
  );
  const ref: any = useRef();
  const firstUpdate = useRef(true);
  const [showPassword, setShowPassword] = useState(false);
  const { register, control, handleSubmit, getValues, formState, reset } =
    useForm({
      resolver: yupResolver(ClientAsUserSchema),
      mode: "onChange",
      reValidateMode: "onChange",
      context: undefined,
      shouldFocusError: true,
      shouldUnregister: false,
      criteriaMode: "firstError",
    });
  const formData = useWatch({ control: control });
  /**
   *
   */
  useEffect(() => {
    if (formData && !firstUpdate.current) {
      props.setContextValue({
        ...formData,
      });
    }
  }, [formData]);

  /**
   * Populate form with user data when context changes
   */
  useEffect(() => {
    if (userContext?.lastName && userContext.lastName !== "") {
      reset(userContext);
      firstUpdate.current = true;
    }
  }, [userContext, reset]);

  /**
   *
   */
  useEffect(
    () => {
      if (firstUpdate.current) {
        firstUpdate.current = false;
        return;
      }
      if (!firstUpdate.current) ref.current.submit();
    },
    [
      // props.shouldHandleSubmit
    ]
  );

  /**
   *
   * @param id
   */
  const updateElement = async (model: any) => {
    const url = `${endpoints.client.updateUserData}/${userContext?._id}`;
    const res = (await updateData(url, model)) as any;
    if (!res || res?.error) {
      return;
    }
    if (props.selectedUser) {
      localStorage.setItem("userName", res?.data?.username);
    }
    props.setContextValue([]);
    props.onClose();
  };

  /**
   *
   * @param id
   */
  const createElement = async (model: any) => {
    const url = `${endpoints.user.main}`;
    const res = (await postData(url, model)) as any;
    if (!res || res?.error) {
      return;
    }
    props.onClose();
    props.setContextValue([]);
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    // If password is provided, mark it as changed
    if (model.password && model.password.trim() !== '') {
      model.changedPassword = true;
    }
    userContext._id ? updateElement(model) : createElement(model);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onFormError)}
      method="get"
      ref={ref}
      id={FormsIds.usersForm}
    >
      <Grid container spacing={Config.standardGridSpacing}>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("lastName")}
            id="lastName"
            name="lastName"
            label={Vocabulary.lastName}
            value={getValues("lastName") || ""}
            fullWidth
            variant="outlined"
            error={typeof formState.errors.lastName === "object"}
            helperText={formState.errors.lastName?.message?.toString()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: "#a5b3c0" }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("firstName")}
            value={getValues("firstName") || ""}
            name="firstName"
            id="firstName"
            label={Vocabulary.firstName}
            fullWidth
            variant="outlined"
            error={typeof formState.errors.firstName === "object"}
            helperText={formState.errors.firstName?.message?.toString()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: "#a5b3c0" }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("email")}
            variant="outlined"
            name="email"
            value={getValues("email") || ""}
            id="email"
            label={Vocabulary.email}
            fullWidth
            error={typeof formState.errors.email === "object"}
            helperText={formState.errors.email?.message?.toString()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: "#a5b3c0" }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("phone")}
            variant="outlined"
            name="phone"
            value={getValues("phone") || ""}
            id="phone"
            label={Vocabulary.phone}
            fullWidth
            error={typeof formState.errors.phone === "object"}
            helperText={formState.errors.phone?.message?.toString()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone sx={{ color: "#a5b3c0" }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("password")}
            variant="outlined"
            id="password"
            name="password"
            type={!showPassword ? "password" : "text"}
            label={Vocabulary.password}
            fullWidth
            error={typeof formState.errors.password === "object"}
            helperText={formState.errors.password?.message?.toString()}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#a5b3c0" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    style={{ color: "#a5b3c0" }}
                    onClick={() => {
                      setShowPassword(!showPassword);
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6}>
          <TextField
            {...register("confirmPassword")}
            variant="outlined"
            id="confirmPassword"
            name="confirmPassword"
            type={!showPassword ? "password" : "text"}
            label={Vocabulary.confirmPassword}
            fullWidth
            error={typeof formState.errors.confirmPassword === "object"}
            helperText={formState.errors.confirmPassword?.message?.toString()}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#a5b3c0" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    style={{ color: "#a5b3c0" }}
                    onClick={() => {
                      setShowPassword(!showPassword);
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </form>
  );
}
